import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TournamentsAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, Plus, ArrowRight, Sparkles } from "lucide-react";
import { getSport } from "@/lib/sports";

export default function TournamentsList() {
  const [searchParams] = useSearchParams();
  const sportFilter = searchParams.get("sport");
  const [allTournaments, setAllTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { settings } = useSettings();

  const tournaments = useMemo(() => {
    const enabled = settings?.enabled_sports;
    if (!enabled || enabled.length === 0) return allTournaments;
    return allTournaments.filter((t) => enabled.includes(t.sport || "football"));
  }, [allTournaments, settings?.enabled_sports]);

  useEffect(() => {
    setLoading(true);
    const params = sportFilter ? { sport: sportFilter } : {};
    TournamentsAPI.list(params)
      .then(setAllTournaments)
      .catch(() => toast.error("Impossible de charger les tournois"))
      .finally(() => setLoading(false));
  }, [sportFilter]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader showSportTabs={true} />
      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pt-16 md:pt-24 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-4"
        >
          <div className="text-overline" style={{ color: "#D4AF37" }}>
            Coupes & Tournois · Laser Tech
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl font-black uppercase tracking-tighter leading-[0.9]">
            La Coupe
            <br />
            <span style={{ color: "#D4AF37" }}>Connectée</span>
          </h1>
          <p className="font-body text-base md:text-lg font-light text-zinc-400 max-w-xl">
            Plus qu'un trophée : une expérience digitale. Scanne le QR du trophée → classement, top players, stats du tournoi.
          </p>
          <div className="pt-2 flex flex-wrap gap-3">
            {user && (
              <Link to="/admin/tournaments/new" data-testid="cta-create-tournament">
                <Button
                  className="text-black rounded-md px-5 h-11 font-body font-medium hover:opacity-90"
                  style={{ backgroundColor: "#D4AF37" }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Créer un tournoi
                </Button>
              </Link>
            )}
            {user && (
              <Link to="/admin/tournaments/import" data-testid="cta-import-tournament">
                <Button
                  variant="outline"
                  className="bg-transparent border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] text-[#D4AF37] rounded-md px-5 h-11"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Importer avec l'IA
                </Button>
              </Link>
            )}
          </div>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-24">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight">
            Tournois
          </h2>
          <span className="text-overline" style={{ color: "#D4AF37" }}>
            {tournaments.length}
          </span>
        </div>
        {loading ? (
          <div className="text-zinc-500 font-body">Chargement...</div>
        ) : tournaments.length === 0 ? (
          <div
            className="border border-dashed border-white/10 rounded-xl p-10 text-center"
            data-testid="empty-tournaments"
          >
            <Trophy className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
            <p className="font-body text-zinc-400">Aucun tournoi créé.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {tournaments.map((t, i) => {
              const sportDef = getSport(t.sport || "football");
              return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={`/tournament/${t.slug}`}
                  data-testid={`tournament-card-${t.slug}`}
                  className="group block relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#111216] to-[#0A0B0E] hover:border-[#D4AF37]/60 transition-all duration-300"
                  style={{ boxShadow: `0 0 0 1px transparent` }}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {t.cover_image_url ? (
                      <img
                        src={t.cover_image_url}
                        alt={t.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] flex items-center justify-center">
                        <Trophy
                          className="w-16 h-16"
                          style={{ color: t.primary_color || "#D4AF37" }}
                        />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                    {/* Sport badge top-right */}
                    <div
                      className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20"
                      data-testid={`tournament-sport-badge-${t.slug}`}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-white"
                        style={{ boxShadow: "0 0 8px #FFFFFF" }}
                      />
                      <span className="text-[10px] uppercase tracking-[0.25em] font-bold metal-text">
                        {sportDef.label}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      {t.season ? (
                        <div
                          className="text-overline mb-1"
                          style={{ color: t.primary_color || "#D4AF37" }}
                        >
                          Saison {t.season}
                        </div>
                      ) : null}
                      <div className="font-display text-3xl font-black uppercase leading-none tracking-tight">
                        {t.name}
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
                    <div className="text-xs font-body text-zinc-400">
                      {t.teams?.length || 0} équipes
                    </div>
                    <ArrowRight
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                      style={{ color: t.primary_color || "#D4AF37" }}
                    />
                  </div>
                </Link>
              </motion.div>
            );
            })}
          </div>
        )}
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-zinc-600 font-body uppercase tracking-[0.25em]">
        <img
          src="/laser-tech-logo.png"
          alt="Laser Tech"
          className="h-14 mx-auto mb-3"
          style={{ mixBlendMode: "screen" }}
        />
        Laser Tech · Création · Genève
      </footer>
    </div>
  );
}
